import { useState } from 'react';
import { assertNoError } from './useOptions';
import { useSubmit } from './usePolicyList';

/**
 * Delete dialog state for rows that group several actions. The user picks which actions to remove
 * (all by default). `confirm` sends one DELETE per action and stops at the first failure; on failure the
 * dialog stays open showing only the actions that are left.
 *
 * `send` deletes one action of a row; `onRemoved` runs once if any action was removed (notify, reload).
 */
export function useActionDelete<T extends { actions: string[] }>(
  send: (row: T, action: string) => Promise<unknown>,
  onRemoved: (row: T, removed: string[]) => Promise<void>
) {
  const [pending, setPending] = useState<T | null>(null);
  const [toRemove, setToRemove] = useState<string[]>([]);
  const { busy, error, setError, submit } = useSubmit();

  const open = (row: T) => {
    setError(null);
    setToRemove(row.actions);
    setPending(row);
  };

  const confirm = async () => {
    if (!pending) return;
    const removed: string[] = [];
    const ok = await submit(async () => {
      for (const name of toRemove) {
        try {
          assertNoError(await send(pending, name));
        } catch (e) {
          throw new Error(`Could not remove ${name}: ${(e as Error).message}`);
        }
        removed.push(name);
      }
    });
    if (ok) {
      // Close before the reload, so the dialog's re-enabled Delete cannot resend the same DELETEs.
      setPending(null);
      await onRemoved(pending, removed);
      return;
    }
    if (removed.length) await onRemoved(pending, removed);
    const left = (a: string) => !removed.includes(a);
    setPending(p => p && { ...p, actions: p.actions.filter(left) });
    setToRemove(sel => sel.filter(left));
  };

  return { pending, toRemove, setToRemove, busy, error, open, close: () => setPending(null), confirm };
}
